import React, { useCallback, useEffect, useState } from 'react';
import { string, bool, func, shape } from 'prop-types';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import localization from '../../../localization';
import { AutocompleteCustom } from '../../../components/Inputs';
import api from '../../../api';

const ActivationLink = ({ onChange, value, isDisabled }) => {
  const [options, setOptions] = useState([]);
  const { enabled = false, providerId = '', qrCodeDisabled = false } = value || {};

  const getOptionsByName = useCallback(
    (searchValue) => api.getCustomerProvidersByName(searchValue)
      .then(({ data }) => data.items), [],
  );

  const handleActivationLinkSwitch = () => {
    onChange({
      ...value,
      enabled: !enabled,
    });
  };

  const handleProviderSelect = useCallback((newProviderId) => {
    onChange({
      providerId: newProviderId,
      enabled: true,
    });
  }, [onChange]);

  useEffect(() => {
    let canceled = false;

    if (enabled && !options.length) {
      api.getCustomerProvidersByName('').then(({ data }) => !canceled && setOptions(data.items));
    }

    return () => {
      canceled = true;
    };
  }, [enabled, options.length]);

  const qrCodeEnabled = !qrCodeDisabled;

  const handleQRCodeDisabledSwitch = () => onChange({
    ...value,
    qrCodeDisabled: !qrCodeDisabled,
  });

  return (
    <Box width={1}>
      <Box width={1} display="flex" alignItems="center">
        <Box display="flex" flexDirection="row" alignItems="center" height="56px">
          <Box p={2}>
            <Typography noWrap color="secondary">
              {`${localization.t('labels.activationLink')} `}
            </Typography>
          </Box>

          <FormControlLabel
            data-test="activationLink"
            control={(
              <Switch
                disabled={isDisabled}
                name="status"
                onChange={handleActivationLinkSwitch}
                color={enabled ? 'success' : 'primary'}
                checked={enabled}
              />
              )}
            label={localization.t(`labels.${enabled ? 'enabled' : 'disabled'}`)}
          />
        </Box>
        {enabled && (
          <AutocompleteCustom
            isDisabled={isDisabled}
            label="provider"
            optionLabelKey="name"
            onSelect={handleProviderSelect}
            curValue={providerId}
            selectOptions={options}
            getAdditionalOptions={getOptionsByName}
          />
        )}
      </Box>
      {enabled && (
        <Box display="flex" alignItems="center" py={2}>
          <Typography noWrap color="secondary" p={2}>
            {`${localization.t('labels.attachQRCodeToEmail')} `}
          </Typography>
          <FormControlLabel
            data-test="qrCodeEnabled"
            control={(
              <Switch
                disabled={isDisabled}
                name="qrCodeEnabled"
                onChange={handleQRCodeDisabledSwitch}
                color={qrCodeEnabled ? 'success' : 'primary'}
                checked={qrCodeEnabled}
              />
              )}
            label={localization.t(`labels.${qrCodeEnabled ? 'enabled' : 'disabled'}`)}
          />
        </Box>
      )}
    </Box>
  );
};

ActivationLink.propTypes = {
  value: shape({ providerId: string, enabled: bool }),
  onChange: func.isRequired,
  isDisabled: bool,
};

export default ActivationLink;
